import type { Logger } from '@/scopes/common/domain/types'
import { mergeDeepRight } from 'ramda'
import {
  CatalogueConfiguration,
  CataloguesConfiguration,
  CatalogueType,
  SiteConfiguration,
  SiteConfigurationWithCatalogues,
} from '@/config/types'
import { CatalogueType as EnumCatalogueType } from '@/scopes/common/domain/enums/CatalogueType'
import { PublicationCodes } from '@/scopes/common/domain/models'
import { Configuration } from '@/scopes/common/domain/types/configuration'
import { getCatalogueType } from '@/scopes/common/infrastructure/config/getCatalogueType'
import { createConsoleLogger } from '@/scopes/common/infrastructure/logging/consoleLogger'

const catalogueTypeToConfigTypeMap: Record<EnumCatalogueType, CatalogueType | undefined> = {
  [EnumCatalogueType.Winter]: 'winter',
  [EnumCatalogueType.Summer]: 'summer',
  [EnumCatalogueType.None]: undefined,
}

export function createConfigRepository({
  map,
  logger = createConsoleLogger(),
}: {
  map: Record<string, () => Promise<{ default: SiteConfigurationWithCatalogues | SiteConfiguration }>>
  logger?: Logger
}) {
  async function get({ siteName, url }: { siteName: string, url?: string }): Promise<Configuration> {
    const retrieveConfig = map[siteName]

    if (!retrieveConfig) {
      logger.logEvent(`No config found for siteName: ${siteName}`)
      // TODO : return NL as default for now, but this should be handled in a better way
      const { default: config } = await map.sunwebnl()
      return reduceConfigurationFromFile(config, url)
    }

    const { default: config } = await retrieveConfig()
    return reduceConfigurationFromFile(config, url)
  }

  return {
    get,
  }
}

function reduceConfigurationFromFile(
  configurationFromFile: SiteConfigurationWithCatalogues | SiteConfiguration,
  url?: string,
) {
  if ('catalogues' in configurationFromFile) {
    const { catalogues, ...configuration } = configurationFromFile as SiteConfigurationWithCatalogues

    const catalogueTypeInFile = getCatalogueType({ url, catalogueList: catalogues })
    const catalogueType = catalogueTypeToConfigTypeMap[catalogueTypeInFile]

    if (catalogueType === undefined) {
      // TODO avoid casting by segregating configurations for pages that catalogue type can not be determined (nor publicationCode) (splash, 404, 500 etc.)
      return {
        ...configuration,
        publicationCode: PublicationCodes.PUBLICATION_CODES.NONE,
      } as Configuration
    }

    const catalogueConfiguration = getCatalogueByType(catalogues, catalogueType)
    const mergedConfiguration = mergeDeepRight(configuration, catalogueConfiguration)
    // TODO avoid casting by segregating configurations for pages that catalogue type can not be determined (nor publicationCode) (splash, 404, 500 etc.)
    return {
      ...mergedConfiguration,
      catalogueType,
    } as Configuration
  }

  return configurationFromFile
}

const map = {
  sunwebnl: () => import('@/config/sunwebnl'),
  sunwebdk: () => import('@/config/sunwebdk'),
  elizabe: () => import('@/config/elizabe'),
  elizanl: () => import('@/config/elizanl'),
  gogonl: () => import('@/config/gogonl'),
  sunwebbefr: () => import('@/config/sunwebbefr'),
  sunwebbenl: () => import('@/config/sunwebbenl'),
  sunwebde: () => import('@/config/sunwebde'),
  sunwebfr: () => import('@/config/sunwebfr'),
  sunwebse: () => import('@/config/sunwebse'),
  sunwebuk: () => import('@/config/sunwebuk'),
}

function getCatalogueByType(catalogues: CataloguesConfiguration, catalogueType: 'summer' | 'winter') {
  return Object.entries<CatalogueConfiguration>(catalogues).find(([type]) => {
    return catalogueType === type
  })?.[1] as CatalogueConfiguration
}

export default createConfigRepository({ map })
